const HHMM = /^\d{2}:\d{2}$/;

function isHhmm(value) {
  return HHMM.test(String(value || "").trim());
}

export function getDayServicePeriods(dayConfig) {
  if (!dayConfig || dayConfig.isOpen === false) return [];
  const periods = [];
  const o1 = String(dayConfig.openTime || "").trim();
  const c1 = String(dayConfig.closeTime || "").trim();
  if (isHhmm(o1) && isHhmm(c1)) {
    periods.push({ openTime: o1, closeTime: c1 });
  }
  const o2 = String(dayConfig.secondOpenTime || "").trim();
  const c2 = String(dayConfig.secondCloseTime || "").trim();
  if (isHhmm(o2) && isHhmm(c2)) {
    periods.push({ openTime: o2, closeTime: c2 });
  }
  return periods.slice(0, 2);
}

export function periodCrossesMidnight(openTime, closeTime) {
  const [openH, openM] = String(openTime).split(":").map(Number);
  const [closeH, closeM] = String(closeTime).split(":").map(Number);
  return closeH < openH || (closeH === openH && closeM <= openM);
}

export function isNowWithinPeriod(openTime, closeTime, now = new Date(), { overnightMorning = false } = {}) {
  const [openH, openM] = String(openTime).split(":").map(Number);
  const [closeH, closeM] = String(closeTime).split(":").map(Number);
  if ([openH, openM, closeH, closeM].some((n) => Number.isNaN(n))) return false;
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const openMins = openH * 60 + openM;
  const closeMins = closeH * 60 + closeM;
  const crosses = periodCrossesMidnight(openTime, closeTime);
  if (!crosses) {
    return nowMins >= openMins && nowMins <= closeMins;
  }
  if (nowMins >= openMins) return true;
  return overnightMorning && nowMins <= closeMins;
}

export function formatDayHoursLabel(dayConfig, formatTime = (t) => t) {
  const periods = getDayServicePeriods(dayConfig);
  if (!periods.length) return "";
  return periods.map((p) => `${formatTime(p.openTime)} – ${formatTime(p.closeTime)}`).join(" · ");
}

export function getDayScheduleStatus(todayConfig, now = new Date(), yesterdayConfig = null) {
  if (yesterdayConfig?.isOpen) {
    const yPeriods = getDayServicePeriods(yesterdayConfig);
    const last = yPeriods[yPeriods.length - 1];
    if (
      last &&
      periodCrossesMidnight(last.openTime, last.closeTime) &&
      isNowWithinPeriod(last.openTime, last.closeTime, now, { overnightMorning: true })
    ) {
      return {
        openNow: true,
        isOpenDay: true,
        afterHours: false,
        currentOpen: last.openTime,
        currentClose: last.closeTime,
        nextOpen: null,
      };
    }
  }

  if (!todayConfig?.isOpen) {
    return {
      openNow: false,
      isOpenDay: false,
      afterHours: false,
      currentOpen: null,
      currentClose: null,
      nextOpen: null,
    };
  }

  const periods = getDayServicePeriods(todayConfig);
  const nowMins = now.getHours() * 60 + now.getMinutes();

  for (const p of periods) {
    if (isNowWithinPeriod(p.openTime, p.closeTime, now)) {
      return {
        openNow: true,
        isOpenDay: true,
        afterHours: false,
        currentOpen: p.openTime,
        currentClose: p.closeTime,
        nextOpen: null,
      };
    }
    const [oh, om] = String(p.openTime).split(":").map(Number);
    const openMins = oh * 60 + om;
    if (nowMins < openMins) {
      return {
        openNow: false,
        isOpenDay: true,
        afterHours: false,
        currentOpen: p.openTime,
        currentClose: p.closeTime,
        nextOpen: p.openTime,
      };
    }
  }

  const last = periods[periods.length - 1] || null;
  return {
    openNow: false,
    isOpenDay: true,
    afterHours: true,
    currentOpen: last?.openTime || null,
    currentClose: last?.closeTime || null,
    nextOpen: null,
  };
}

/** Same hour-block builder as checkout / plan-for-today. */
export function generateHoursForPeriod(openTime, closeTime) {
  const [openH] = String(openTime).split(":").map(Number);
  const [closeH, closeM] = String(closeTime).split(":").map(Number);
  const hours = [];
  let currentH = openH;
  const crossesMidnight = periodCrossesMidnight(openTime, closeTime);

  while (true) {
    const nextH = (currentH + 1) % 24;
    const label = `${currentH.toString().padStart(2, "0")}:00 - ${nextH.toString().padStart(2, "0")}:00`;

    if (!crossesMidnight) {
      if (currentH === closeH && closeM === 0) break;
      if (currentH === closeH && closeM > 0) break;
      if (currentH > closeH) break;
    } else if (currentH === closeH && closeM === 0) {
      break;
    } else if (currentH === closeH && closeM > 0) {
      break;
    }

    hours.push({ hour: currentH, label });

    if (!crossesMidnight && nextH > closeH) break;
    if (crossesMidnight && currentH === closeH) break;

    currentH = nextH;
    if (currentH === openH) break;
  }

  if (closeM > 0 && !hours.some((h) => h.hour === closeH)) {
    let label;
    if (closeM < 40) {
      label = `${closeH.toString().padStart(2, "0")}:00 - ${closeH.toString().padStart(2, "0")}:30`;
    } else {
      const nextH = (closeH + 1) % 24;
      label = `${closeH.toString().padStart(2, "0")}:00 - ${nextH.toString().padStart(2, "0")}:00`;
    }
    hours.push({ hour: closeH, label });
  }
  return hours;
}

export function filterHoursForPeriod(hours, openTime, closeTime, now = new Date()) {
  const [openH] = String(openTime).split(":").map(Number);
  const [closeH] = String(closeTime).split(":").map(Number);
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();
  const crossesMidnight = periodCrossesMidnight(openTime, closeTime);
  let lastActiveHour = closeH - 1;
  if (lastActiveHour < 0) lastActiveHour = 23;
  const isInLastActiveHour = currentHour === lastActiveHour;
  let logicalStartHour = !isInLastActiveHour && currentMinutes >= 48 ? currentHour + 1 : currentHour;
  if (logicalStartHour >= 24) logicalStartHour = 0;

  let startHour;
  if (!crossesMidnight) {
    if (logicalStartHour < openH) startHour = openH;
    else if (logicalStartHour > closeH) startHour = closeH;
    else startHour = logicalStartHour;
  } else if (logicalStartHour >= openH || logicalStartHour <= closeH) {
    startHour = logicalStartHour;
  } else {
    startHour = openH;
  }

  return hours.filter((h) => {
    if (!crossesMidnight) return h.hour >= startHour;
    return h.hour >= startHour || (h.hour <= closeH && logicalStartHour > openH);
  });
}

export function buildAvailablePickupHours(days, now = new Date()) {
  if (!Array.isArray(days)) return [];
  const weekday = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  const yesterdayWeekday = y.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const todayConfig = days.find((d) => d.day === weekday);
  const yesterdayConfig = days.find((d) => d.day === yesterdayWeekday);

  const result = [];
  const seen = new Set();
  const pushHours = (hours) => {
    for (const h of hours) {
      if (seen.has(h.hour)) continue;
      seen.add(h.hour);
      result.push(h);
    }
  };

  if (yesterdayConfig?.isOpen) {
    const periods = getDayServicePeriods(yesterdayConfig);
    const last = periods[periods.length - 1];
    if (
      last &&
      periodCrossesMidnight(last.openTime, last.closeTime) &&
      isNowWithinPeriod(last.openTime, last.closeTime, now, { overnightMorning: true })
    ) {
      const yHours = generateHoursForPeriod(last.openTime, last.closeTime);
      const [openH] = last.openTime.split(":").map(Number);
      const leftover = yHours.filter((h) => h.hour < openH);
      pushHours(filterHoursForPeriod(leftover, last.openTime, last.closeTime, now));
    }
  }

  if (todayConfig?.isOpen) {
    for (const p of getDayServicePeriods(todayConfig)) {
      pushHours(filterHoursForPeriod(generateHoursForPeriod(p.openTime, p.closeTime), p.openTime, p.closeTime, now));
    }
  }

  return result;
}

export function weekdayKeyFromDate(now = new Date()) {
  return now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
}

export function yesterdayConfigFromDays(days, now = new Date()) {
  if (!Array.isArray(days)) return null;
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  const key = y.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  return days.find((d) => d.day === key) || null;
}
